// src/features/devices/deviceHistorySlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { IRootState } from "../../app/store"; // juster path
import { IDevice } from "@multiconsult-group/multiplacer-core";
/**
 * Nivå A: Vi lagrer hele "before" og "after" device-objektet (IDevice).
 * Dette gjør undo/redo automatisk kompatibelt med "alt" senere.
 */

export type IDeviceId = string;

export type IDeviceUpdateCommand = {
  type: "UPDATE_DEVICE";
  deviceId: IDeviceId;
  before: IDevice;
  after: IDevice;
  meta?: {
    createdAtIso?: string;
    projectNo?: string;
    roomNumber?: string;
    reason?: "MOVE" | "EDIT" | "OTHER";
  };
};

export interface IDeviceHistoryState {
  undoStack: IDeviceUpdateCommand[];
  redoStack: IDeviceUpdateCommand[];

  isApplyingHistory: boolean;

  /**
   * Midlertidig storage for "before" snapshot ved drag-start.
   */
  pendingBeforeByDeviceId: Record<IDeviceId, IDevice | undefined>;

  maxStackSize: number;
}

/* -------------------------------------------------------------------------- */
/* Utils                                                                       */
/* -------------------------------------------------------------------------- */

const deepCloneDevice = (d: IDevice): IDevice => {
  // structuredClone er best hvis tilgjengelig (bevarer mer korrekt).
  // Fallback til JSON (ok for rene data-objekter).
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (typeof structuredClone === "function") {
    return structuredClone(d);
  }
  return JSON.parse(JSON.stringify(d)) as IDevice;
};

const trimStack = <T>(arr: T[], max: number): T[] => {
  if (arr.length <= max) return arr;
  return arr.slice(arr.length - max);
};

const requireDeviceId = (device: IDevice): string => {
  // ZDevice tillater _id optional, men i praksis må en eksisterende device ha _id.
  // Vi feiler tidlig for å unngå at history blir inkonsistent.
  if (!device._id) {
    throw new Error("Device _id is missing (cannot track history without deviceId).");
  }
  // _id kan være ObjectId-ish; vi trenger en stabil string key.
  return String(device._id);
};

/* -------------------------------------------------------------------------- */
/* Initial state                                                               */
/* -------------------------------------------------------------------------- */

const initialState: IDeviceHistoryState = {
  undoStack: [],
  redoStack: [],
  isApplyingHistory: false,
  pendingBeforeByDeviceId: {},
  maxStackSize: 100,
};

/* -------------------------------------------------------------------------- */
/* Slice                                                                       */
/* -------------------------------------------------------------------------- */

export const deviceHistorySlice = createSlice({
  name: "deviceHistory",
  initialState,
  reducers: {
    setIsApplyingHistoryInRedux(state, action: PayloadAction<boolean>) {
      state.isApplyingHistory = action.payload;
    },

    setMaxStackSizeInRedux(state, action: PayloadAction<number>) {
      state.maxStackSize = Math.max(1, action.payload);
      state.undoStack = trimStack(state.undoStack, state.maxStackSize);
      state.redoStack = trimStack(state.redoStack, state.maxStackSize);
    },

    clearHistoryInRedux(state) {
      state.undoStack = [];
      state.redoStack = [];
      state.pendingBeforeByDeviceId = {};
      state.isApplyingHistory = false;
    },

    /**
     * Kalles på drag-start: lagrer "before" snapshot av hele device.
     */
    captureBeforeInRedux(state, action: PayloadAction<{ device: IDevice }>) {
      const device = action.payload.device;
      const deviceId = requireDeviceId(device);
      state.pendingBeforeByDeviceId[deviceId] = deepCloneDevice(device);
    },

    clearBefore(state, action: PayloadAction<{ deviceId: IDeviceId }>) {
      delete state.pendingBeforeByDeviceId[action.payload.deviceId];
    },

    /**
     * Push en command til undo-stack og clear redo-stack.
     * (Skal ikke brukes mens vi er i isApplyingHistory)
     */
    pushCommandInRedux(state, action: PayloadAction<IDeviceUpdateCommand>) {
      if (state.isApplyingHistory) return;

      state.undoStack.push(action.payload);
      state.undoStack = trimStack(state.undoStack, state.maxStackSize);

      state.redoStack = [];
    },

    /**
     * Stack-operasjon: pop fra undo og push til redo.
     */
    popUndoToRedoInRedux(state) {
      const cmd = state.undoStack.pop();
      if (!cmd) return;
      state.redoStack.push(cmd);
      state.redoStack = trimStack(state.redoStack, state.maxStackSize);
    },

    /**
     * Stack-operasjon: pop fra redo og push til undo.
     */
    popRedoToUndoInRedux(state) {
      const cmd = state.redoStack.pop();
      if (!cmd) return;
      state.undoStack.push(cmd);
      state.undoStack = trimStack(state.undoStack, state.maxStackSize);
    },
  },
});

export const {
  setIsApplyingHistoryInRedux,
  setMaxStackSizeInRedux,
  clearHistoryInRedux,
  captureBeforeInRedux,
  clearBefore,
  pushCommandInRedux,
  popUndoToRedoInRedux,
  popRedoToUndoInRedux,
} = deviceHistorySlice.actions;

export const deviceHistoryReducer = deviceHistorySlice.reducer;

/* -------------------------------------------------------------------------- */
/* Selectors                                                                   */
/* -------------------------------------------------------------------------- */

export const selectDeviceHistory = (s: IRootState): IDeviceHistoryState => s.deviceHistory;

export const selectCanUndo = (s: IRootState): boolean =>
  selectDeviceHistory(s).undoStack.length > 0;
export const selectCanRedo = (s: IRootState): boolean =>
  selectDeviceHistory(s).redoStack.length > 0;

export const selectPendingBeforeForDevice =
  (deviceId: IDeviceId) =>
  (s: IRootState): IDevice | undefined =>
    selectDeviceHistory(s).pendingBeforeByDeviceId[deviceId];

export const selectTopUndoCommand = (s: IRootState): IDeviceUpdateCommand | undefined => {
  const { undoStack } = selectDeviceHistory(s);
  return undoStack[undoStack.length - 1];
};

export const selectTopRedoCommand = (s: IRootState): IDeviceUpdateCommand | undefined => {
  const { redoStack } = selectDeviceHistory(s);
  return redoStack[redoStack.length - 1];
};

/* -------------------------------------------------------------------------- */
/* Helpers (optional)                                                         */
/* -------------------------------------------------------------------------- */

export const deviceHistoryUtils = {
  requireDeviceId,
};
